import React, { useMemo, useState } from 'react';
import { computeBudget, fmtEUR } from '../calculations/budgetCalculations.js';
import { computeCuveCost, summarizeCuve } from '../calculations/cuveCalculations.js';
import { computePompageCost, summarizePompage } from '../calculations/pompageCalculations.js';
import { computeFiltrationCost, summarizeFiltration } from '../calculations/filtrationCalculations.js';
import { computePlomberieCost, summarizePlomberie } from '../calculations/plomberieCalculations.js';
import { computeUsageSavings } from '../calculations/roiCalculations.js';
import AccordionSection from './budget/AccordionSection.js';
import CuveSection from './budget/CuveSection.js';
import PompageSection from './budget/PompageSection.js';
import FiltrationSection from './budget/FiltrationSection.js';
import PlomberieSection from './budget/PlomberieSection.js';

/**
 * Étape 6 : estimation budgétaire, organisée en 4 domaines présentés en accordéon exclusif
 * (une seule section ouverte à la fois). Terminer une section ("Continuer") la replie et
 * ouvre la suivante dans l'ordre fixe. Chaque section affiche son prix en direct dans son
 * en-tête, repliée ou non. Le résultat global (breakdown + total) reste unique, sous les
 * sections, alimenté par le budget composé des 4 domaines.
 */
const BudgetStep = ({
  usages, budgetState, setBudgetState, currentStep, totalSteps, goPrevious, goNext, canAdvance,
  selectedTankCoverage, waterPrice,
}) => {
  const [openSection, setOpenSection] = useState('cuve');
  const budget = useMemo(() => computeBudget(budgetState), [budgetState]);

  // Même formule que l'étape ROI (computeUsageSavings), pour que les deux étapes
  // affichent toujours le même chiffre d'économie annuelle — plutôt qu'une
  // heuristique déconnectée basée uniquement sur le volume de cuve.
  const { totalEurosSaved } = useMemo(
    () => computeUsageSavings(usages, selectedTankCoverage ?? 0, waterPrice.dist, waterPrice.sewage),
    [usages, selectedTankCoverage, waterPrice]
  );

  const setCuve = (patch) => setBudgetState(prev => ({ ...prev, cuve: { ...prev.cuve, ...patch } }));
  const setPompage = (patch) => setBudgetState(prev => ({ ...prev, pompage: { ...prev.pompage, ...patch } }));
  const setFiltration = (patch) => setBudgetState(prev => ({ ...prev, filtration: { ...prev.filtration, ...patch } }));
  const setPlomberie = (patch) => setBudgetState(prev => ({ ...prev, plomberie: { ...prev.plomberie, ...patch } }));

  const continueTo = (next) => setOpenSection(next);
  const toggleSection = (key) => setOpenSection(prev => (prev === key ? null : key));

  const cuveCost = useMemo(() => computeCuveCost(budgetState.cuve), [budgetState.cuve]);
  const pompageCost = useMemo(() => computePompageCost(budgetState.pompage), [budgetState.pompage]);
  const filtrationCost = useMemo(() => computeFiltrationCost(budgetState.filtration), [budgetState.filtration]);
  const plomberieCost = useMemo(() => computePlomberieCost(budgetState.plomberie), [budgetState.plomberie]);

  return (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">Récupération d'eau de pluie</span>
          <h2 className="apv2-step-heading">Estimation budgétaire</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {totalSteps}</span>
      </div>
      <div className="apv2-step-body">

        <div className="apv2-warning-banner">
          Les montants ci-dessous sont des ordres de grandeur génériques (non rattachés à des tarifs de prestataires réels) — à affiner avec des devis.
        </div>

        <div className="rw-accordion">
          <AccordionSection
            title="Cuve"
            summary={summarizeCuve(budgetState.cuve)}
            priceLow={cuveCost.low} priceHigh={cuveCost.high}
            isOpen={openSection === 'cuve'}
            onHeaderClick={() => toggleSection('cuve')}
            onContinue={() => continueTo('pompage')}
          >
            <CuveSection cuve={budgetState.cuve} setCuve={setCuve} />
          </AccordionSection>

          <AccordionSection
            title="Pompage & surpression"
            summary={summarizePompage(budgetState.pompage)}
            priceLow={pompageCost.low} priceHigh={pompageCost.high}
            isOpen={openSection === 'pompage'}
            onHeaderClick={() => toggleSection('pompage')}
            onContinue={() => continueTo('filtration')}
          >
            <PompageSection pompage={budgetState.pompage} setPompage={setPompage} usages={usages} />
          </AccordionSection>

          <AccordionSection
            title="Filtration"
            summary={summarizeFiltration(budgetState.filtration)}
            priceLow={filtrationCost.low} priceHigh={filtrationCost.high}
            isOpen={openSection === 'filtration'}
            onHeaderClick={() => toggleSection('filtration')}
            onContinue={() => continueTo('plomberie')}
          >
            <FiltrationSection filtration={budgetState.filtration} setFiltration={setFiltration} usages={usages} />
          </AccordionSection>

          <AccordionSection
            title="Plomberie & installation"
            summary={summarizePlomberie(budgetState.plomberie)}
            priceLow={plomberieCost.low} priceHigh={plomberieCost.high}
            isOpen={openSection === 'plomberie'}
            onHeaderClick={() => toggleSection('plomberie')}
          >
            <PlomberieSection plomberie={budgetState.plomberie} setPlomberie={setPlomberie} />
          </AccordionSection>
        </div>

        <div className="rw-result-area">
          <div className="rw-result-title">Estimation budgétaire <span className="rw-badge-eco">Économie eau potable estimée</span></div>
          <div className="apv2-summary-cards">
            <div className="apv2-summary-card">
              <span className="apv2-summary-value">{fmtEUR(budget.totalLow)} €</span>
              <span className="apv2-summary-label">fourchette basse TTC</span>
            </div>
            <div className="apv2-summary-card">
              <span className="apv2-summary-value">{fmtEUR(budget.totalHigh)} €</span>
              <span className="apv2-summary-label">fourchette haute TTC</span>
            </div>
            <div className="apv2-summary-card apv2-summary-card--secondary">
              <span className="apv2-summary-value">{fmtEUR(totalEurosSaved)} €</span>
              <span className="apv2-summary-label">économie annuelle estimée</span>
            </div>
          </div>

          <div className="rw-breakdown">
            {budget.breakdown.map((r, i) => (
              <div className="rw-brow" key={i}>
                <span className="rw-bname">{r.name}</span>
                <span className="rw-bval">{fmtEUR(r.low)} – {fmtEUR(r.high)} €</span>
              </div>
            ))}
            <div className="rw-total-row">
              <span className="rw-tname">Total estimé TTC</span>
              <span className="rw-tval">{fmtEUR(budget.totalLow)} – {fmtEUR(budget.totalHigh)} €</span>
            </div>
          </div>
        </div>
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
          Suivant →
        </button>
      </div>
    </div>
  );
};

export default BudgetStep;
